import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import authenticatedAxios from '../../authenticatedAxios';
import { baseurl } from '../../api';
import { toast } from 'react-toastify';
import './Accounting.css';

const AccountingConfigurationEdit = () => {
  const navigate = useNavigate();
  const [journals, setJournals] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    sales_journal: '',
    purchase_journal: '',
    bank_journal: '',
    cash_journal: '',
    miscellaneous_journal: '',

    exchange_gain_account: '',
    exchange_loss_account: '',
    stock_valuation_account: '',
    stock_adjustment_account: '',
    cogs_account: '',
    default_accounts_receivable: '',
    default_accounts_payable: '',
    customer_advance_account: '',
    vendor_advance_account: '',
    tax_payable_account: '',
    tax_receivable_account: '',
    rounding_account: '',
    suspense_account: '',
    retained_earnings_account: '',
    opening_balance_equity_account: '',
    grni_account: '',
    discount_expense_account: '',
    discount_income_account: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch Master Journals
        const journalRes = await authenticatedAxios.get(`${baseurl}accounting/master-journal/`);
        const journalList = journalRes?.data?.data || journalRes?.data?.results || [];
        setJournals(journalList);

        // Fetch Accounts
        const accountRes = await authenticatedAxios.get(`${baseurl}accounting/chartofaccount/`);
        const accountList = accountRes?.data?.data || accountRes?.data?.results || [];
        setAccounts(accountList);

        // Fetch Current Master Config
        const configRes = await authenticatedAxios.get(`${baseurl}accounting/master-accounting-configuration/`);
        if (configRes?.data && configRes?.data?.data) {
          const c = configRes.data.data;
          setFormData({
            sales_journal: c.sales_journal || '',
            purchase_journal: c.purchase_journal || '',
            bank_journal: c.bank_journal || '',
            cash_journal: c.cash_journal || '',
            miscellaneous_journal: c.miscellaneous_journal || '',

            exchange_gain_account: c.exchange_gain_account || '',
            exchange_loss_account: c.exchange_loss_account || '',
            stock_valuation_account: c.stock_valuation_account || '',
            stock_adjustment_account: c.stock_adjustment_account || '',
            cogs_account: c.cogs_account || '',
            default_accounts_receivable: c.default_accounts_receivable || '',
            default_accounts_payable: c.default_accounts_payable || '',
            customer_advance_account: c.customer_advance_account || '',
            vendor_advance_account: c.vendor_advance_account || '',
            tax_payable_account: c.tax_payable_account || '',
            tax_receivable_account: c.tax_receivable_account || '',
            rounding_account: c.rounding_account || '',
            suspense_account: c.suspense_account || '',
            retained_earnings_account: c.retained_earnings_account || '',
            opening_balance_equity_account: c.opening_balance_equity_account || '',
            grni_account: c.grni_account || '',
            discount_expense_account: c.discount_expense_account || '',
            discount_income_account: c.discount_income_account || '',
          });
        }
      } catch (error) {
        console.error('Error loading configuration edit data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? null : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await authenticatedAxios.put(`${baseurl}accounting/master-accounting-configuration/`, formData);
      if (response?.data) {
        toast.success('Configuration saved successfully!');
        navigate('/accounting/configuration');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="whole-container">
        <PageHeader pageName="Accounting / Configurations /" nestedpageName="Edit" linkTo="/accounting/configuration" />
        <div className="accounting-config-container">
          <p className="common-text fts-14 ftw-4">Loading form data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader pageName="Accounting / Configurations /" nestedpageName="Edit" linkTo="/accounting/configuration" />
      </div>

      <form onSubmit={handleSubmit} className="accounting-config-container">
        <div className="accounting-sub-header">
          Manage the default accounting configuration for your organization.
        </div>

        {/* DEFAULT JOURNALS */}
        <div className="accounting-card">
          <div className="accounting-card-title">DEFAULT JOURNALS</div>
          
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default sales journal</label>
            <select
              name="sales_journal"
              className="accounting-select-box"
              value={formData.sales_journal || ''}
              onChange={handleChange}
            >
              <option value="">Select journal</option>
              {journals.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Purchase journal</label>
            <select
              name="purchase_journal"
              className="accounting-select-box"
              value={formData.purchase_journal || ''}
              onChange={handleChange}
            >
              <option value="">Select journal</option>
              {journals.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Bank journal</label>
            <select
              name="bank_journal"
              className="accounting-select-box"
              value={formData.bank_journal || ''}
              onChange={handleChange}
            >
              <option value="">Select journal</option>
              {journals.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Cash journal</label>
            <select
              name="cash_journal"
              className="accounting-select-box"
              value={formData.cash_journal || ''}
              onChange={handleChange}
            >
              <option value="">Select journal</option>
              {journals.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Miscellaneous journal</label>
            <select
              name="miscellaneous_journal"
              className="accounting-select-box"
              value={formData.miscellaneous_journal || ''}
              onChange={handleChange}
            >
              <option value="">Select journal</option>
              {journals.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* CURRENCY */}
        <div className="accounting-card">
          <div className="accounting-card-title">CURRENCY</div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Exchange gain account</label>
            <select
              name="exchange_gain_account"
              className="accounting-select-box"
              value={formData.exchange_gain_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Exchange loss account</label>
            <select
              name="exchange_loss_account"
              className="accounting-select-box"
              value={formData.exchange_loss_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* INVENTORY */}
        <div className="accounting-card">
          <div className="accounting-card-title">INVENTORY</div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Stock valuation account</label>
            <select
              name="stock_valuation_account"
              className="accounting-select-box"
              value={formData.stock_valuation_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Opening stock adjustment account</label>
            <select
              name="stock_adjustment_account"
              className="accounting-select-box"
              value={formData.stock_adjustment_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">COGS Account</label>
            <select
              name="cogs_account"
              className="accounting-select-box"
              value={formData.cogs_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">GRNI Account</label>
            <select
              name="grni_account"
              className="accounting-select-box"
              value={formData.grni_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* RECEIVABLES & PAYABLES */}
        <div className="accounting-card">
          <div className="accounting-card-title">RECEIVABLES &amp; PAYABLES</div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Default accounts receivable</label>
            <select
              name="default_accounts_receivable"
              className="accounting-select-box"
              value={formData.default_accounts_receivable || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Default accounts payable</label>
            <select
              name="default_accounts_payable"
              className="accounting-select-box"
              value={formData.default_accounts_payable || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Customer advance account</label>
            <select
              name="customer_advance_account"
              className="accounting-select-box"
              value={formData.customer_advance_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Vendor advance account</label>
            <select
              name="vendor_advance_account"
              className="accounting-select-box"
              value={formData.vendor_advance_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* TAXES */}
        <div className="accounting-card">
          <div className="accounting-card-title">TAXES</div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Tax payable account</label>
            <select
              name="tax_payable_account"
              className="accounting-select-box"
              value={formData.tax_payable_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Tax receivable account</label>
            <select
              name="tax_receivable_account"
              className="accounting-select-box"
              value={formData.tax_receivable_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* OTHERS */}
        <div className="accounting-card">
          <div className="accounting-card-title">OTHERS</div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Suspense account</label>
            <select
              name="suspense_account"
              className="accounting-select-box"
              value={formData.suspense_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Retained earnings</label>
            <select
              name="retained_earnings_account"
              className="accounting-select-box"
              value={formData.retained_earnings_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Rounding account</label>
            <select
              name="rounding_account"
              className="accounting-select-box"
              value={formData.rounding_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Opening balance account</label>
            <select
              name="opening_balance_equity_account"
              className="accounting-select-box"
              value={formData.opening_balance_equity_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Discount expenses</label>
            <select
              name="discount_expense_account"
              className="accounting-select-box"
              value={formData.discount_expense_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="accounting-field-group">
            <label className="accounting-field-label">Discount income</label>
            <select
              name="discount_income_account"
              className="accounting-select-box"
              value={formData.discount_income_account || ''}
              onChange={handleChange}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ${a.account_name}` : a.account_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginTop: '24px', marginBottom: '40px', display: 'flex', justifyContent: 'flex-start' }}>
          <button
            type="button"
            className="accounting-btn-secondary"
            onClick={() => navigate('/accounting/configuration')}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="accounting-btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountingConfigurationEdit;
